import React, { memo, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

import {
  Attachments,
  CareerCenter,
  CareerLearning,
  CareerLesson,
  CareerVacancies,
  Course,
  Courses,
  Diplomas,
  Login,
  ErrorPage,
  Invoices,
  Growth,
  GrowthMedals,
  GrowthProfession,
  GrowthProfessionList,
  GrowthSkill,
  GrowthSkills,
  Resume,
  SignUp,
  SurveyFill,
  Surveys,
} from 'pages';
import { MainTemplate, AttachmentsTemplate } from 'components/templates';

import { useReactiveVar } from '@apollo/client';

import { appState, authState } from 'core/state';
import { ScrollToTop } from 'core/hooks/Routes/useScrollToTop';
import { PATHS } from './config';

export const AppRouter = memo(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { networkError } = useReactiveVar(appState);
  const { isLoggedIn } = useReactiveVar(authState);
  const currentState = appState();

  useEffect(() => {
    if (networkError) {
      navigate(`/${networkError}`);
      appState({
        ...currentState,
        networkError: null,
      });
    }
  }, [navigate, networkError, currentState]);

  useEffect(() => {
    if (!isLoggedIn && location.pathname !== PATHS.signUp) {
      navigate(PATHS.login);
    }
    if (isLoggedIn && location.pathname === PATHS.signUp) {
      navigate(PATHS.courses);
    }
  }, [isLoggedIn, navigate, location.pathname]);

  return (
    <ScrollToTop>
      <Routes>
        <Route path={PATHS.main} element={<Navigate to={PATHS.courses} />} />
        <Route
          path={PATHS.courses}
          element={
            <MainTemplate>
              <Courses />
            </MainTemplate>
          }
        />
        <Route
          path={PATHS.course}
          element={
            <MainTemplate>
              <Course />
            </MainTemplate>
          }
        />
        <Route
          path={PATHS.lesson}
          element={
            <MainTemplate>
              <Course />
            </MainTemplate>
          }
        />

        <Route
          path={PATHS.resume}
          element={
            <MainTemplate>
              <Resume />
            </MainTemplate>
          }
        />

        {/*
          ТЗ 0039, доводка UX: подстраницы «Моего роста». Порядок — от частного
          к общему, как и у /career выше: /growth/skills/:slug должен
          сматчиться раньше, чем /growth/skills.

          Гейта членства нет и здесь: это тот же продающий раздел, разложенный
          по адресам. Гард по роли — внутри страниц.
        */}
        <Route
          path={PATHS.growthAchievements}
          element={
            <MainTemplate>
              <GrowthMedals />
            </MainTemplate>
          }
        />

        <Route
          path={PATHS.growthProfession}
          element={
            <MainTemplate>
              <GrowthProfession />
            </MainTemplate>
          }
        />

        <Route
          path={PATHS.growthProfessions}
          element={
            <MainTemplate>
              <GrowthProfessionList />
            </MainTemplate>
          }
        />

        <Route
          path={PATHS.growthSkill}
          element={
            <MainTemplate>
              <GrowthSkill />
            </MainTemplate>
          }
        />

        <Route
          path={PATHS.growthSkills}
          element={
            <MainTemplate>
              <GrowthSkills />
            </MainTemplate>
          }
        />

        <Route
          // ТЗ 0039 шаг 4. БЕЗ гейта членства в карьерном центре — экран
          // продающий, и закрыть его от тех, кто ещё учится, значит показать
          // его только тем, кому продавать уже нечего.
          path={PATHS.growth}
          element={
            <MainTemplate>
              <Growth />
            </MainTemplate>
          }
        />

        <Route
          path={PATHS.invoices}
          element={
            <MainTemplate>
              <Invoices />
            </MainTemplate>
          }
        />

        <Route
          path={PATHS.diplomas}
          element={
            <MainTemplate>
              <Diplomas />
            </MainTemplate>
          }
        />

        <Route
          path={PATHS.surveys}
          element={
            <MainTemplate>
              <Surveys />
            </MainTemplate>
          }
        />

        <Route
          path={PATHS.survey}
          element={
            <MainTemplate>
              <SurveyFill />
            </MainTemplate>
          }
        />

        {/*
          Career Center (spec 0018). Access is gated inside the pages via
          useCareerAccessGuard — a non-member landing here by URL is sent to
          404, and the server independently refuses the data anyway.
          Most specific path first: /career/learning/:lessonId must be matched
          before /career/learning, and both /career/* before /career.
        */}
        <Route
          path={PATHS.careerLesson}
          element={
            <MainTemplate>
              <CareerLesson />
            </MainTemplate>
          }
        />

        <Route
          path={PATHS.careerLearning}
          element={
            <MainTemplate>
              <CareerLearning />
            </MainTemplate>
          }
        />

        <Route
          path={PATHS.careerVacancies}
          element={
            <MainTemplate>
              <CareerVacancies />
            </MainTemplate>
          }
        />

        <Route
          path={PATHS.careerCenter}
          element={
            <MainTemplate>
              <CareerCenter />
            </MainTemplate>
          }
        />

        <Route
          path={PATHS.attachment}
          element={
            <AttachmentsTemplate>
              <Attachments />
            </AttachmentsTemplate>
          }
        />

        <Route path={PATHS.signUp} key={PATHS.signUp} element={<SignUp />} />
        <Route path={PATHS.login} key={PATHS.login} element={<Login />} />
        <Route path="*" element={<ErrorPage status={404} />} />
        <Route path="/404" element={<ErrorPage status={404} />} />
        <Route path="/500" element={<ErrorPage status={500} />} />
        <Route path="/503" element={<ErrorPage status={503} />} />
        <Route path="/error" element={<ErrorPage status="error" />} />
      </Routes>
    </ScrollToTop>
  );
});
